import { useState, useEffect, useCallback } from "react";
import type { PendingMemberDto } from "../../../api/types";
import { apiGet, apiPost } from "../../../api/http";
import { fetchMemberLevelsForTenant } from "../../../api/memberLevels";
import { fetchEnrollmentFormFields } from "../../../api/enrollmentFormFields";
import { assocBasicInfoFromApi, fetchTenantProfile } from "../../../api/tenantProfile";
import { useAdminData } from "../AdminDataContext";
import { memberLevelStore, type MemberLevel } from "../utils/memberLevelStore";
import { notificationStore } from "../utils/notificationStore";
import { basicInfoStore, type AssocBasicInfo } from "../utils/basicInfoStore";
import { buildAuditFeePaymentRows, enrollmentAssocLabel } from "../../member-services/utils/enrollmentCopy";
import AgreementEnrollmentPreviewModal from "./AgreementEnrollmentPreviewModal";
import { reportError, showUserNotice } from "../../../api/errors";
import { wxBindBadge } from "../utils/wxBindDisplay";

type AuditStatus = "pending" | "approved" | "rejected";
type AuditListTab = "pending" | "rejected";

interface AuditItem extends PendingMemberDto {
  status: AuditStatus;
  assignedLevel?: string;
  assignedMemberNo?: string;
}

type DetailTab = "info" | "signature" | "payment";

function MockSignature() {
  return (
    <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-3 flex flex-col items-center justify-center">
      <svg viewBox="0 0 400 120" className="w-full max-w-[220px] h-14">
        <path
          d="M30,80 C50,50 60,30 80,55 C95,70 100,55 120,45 C140,35 145,65 165,55 C180,47 185,65 205,55 C220,47 225,70 245,60 L265,48 C280,40 285,60 305,52 C320,46 330,70 350,60"
          stroke="#1a1a1a"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <p className="text-[11px] text-gray-400 mt-1.5">电子手签名（示意）</p>
    </div>
  );
}

function deriveMemberLegacyId(applicationLegacyId: string): string {
  const tail = applicationLegacyId.replace(/[^a-zA-Z0-9-]/g, "");
  return tail.startsWith("u") ? tail : `u-${tail}`;
}

function normalizeExtraAnswers(raw: PendingMemberDto["extraAnswers"]): Record<string, string> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v === undefined || v === null) continue;
    out[k] = typeof v === "string" ? v : typeof v === "number" || typeof v === "boolean" ? String(v) : JSON.stringify(v);
  }
  return out;
}

export default function MemberAudit() {
  const { memberApplications, tenantLegacyId, reload, storeVersion } = useAdminData();
  const [items, setItems] = useState<AuditItem[]>([]);
  const [rejectedItems, setRejectedItems] = useState<AuditItem[]>([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [listTab, setListTab] = useState<AuditListTab>("pending");
  const [basicInfo, setBasicInfo] = useState<AssocBasicInfo>(() => basicInfoStore.get());
  const [enrollmentLabels, setEnrollmentLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    fetchEnrollmentFormFields(tenantLegacyId)
      .then((rows) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const r of rows) map[r.id] = r.label;
        setEnrollmentLabels(map);
      })
      .catch(() => {
        if (!cancelled) setEnrollmentLabels({});
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  useEffect(() => {
    let cancelled = false;
    void fetchTenantProfile(tenantLegacyId)
      .then((d) => {
        if (cancelled) return;
        const next = assocBasicInfoFromApi(d);
        setBasicInfo(next);
        basicInfoStore.save(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  useEffect(() => {
    setItems(memberApplications.map((m) => ({ ...m, status: "pending" as AuditStatus })));
  }, [memberApplications]);

  const loadRejected = useCallback(async () => {
    setRejectedLoading(true);
    try {
      const rows = await apiGet<PendingMemberDto[]>(
        `/api/tenants/${encodeURIComponent(tenantLegacyId)}/member-applications?reviewStatus=rejected`,
      );
      setRejectedItems(
        (Array.isArray(rows) ? rows : []).map((m) => ({
          ...m,
          status: "rejected" as AuditStatus,
        })),
      );
    } catch (e) {
      setRejectedItems([]);
      reportError(e, "加载已拒绝申请失败");
    } finally {
      setRejectedLoading(false);
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    void loadRejected();
  }, [loadRejected]);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>("info");
  const [showFullAgreement, setShowFullAgreement] = useState<{
    name: string;
    date: string;
    memberNo?: string | null;
    idNumber?: string | null;
    phone?: string | null;
    signatureUrl: string | null;
    agreementSnapshot?: {
      title?: string | null;
      content?: string | null;
      version?: string | null;
    } | null;
  } | null>(null);
  const [auditSigImgFailed, setAuditSigImgFailed] = useState(false);

  // ── Inline approval state (used inside the detail modal) ──
  const [approvalLevel, setApprovalLevel] = useState<string>("");
  const [showApprovalPanel, setShowApprovalPanel] = useState(false);

  // ── Batch selection state ──
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchApproving, setBatchApproving] = useState(false);
  const [batchLevel, setBatchLevel] = useState<string>("");
  /** 非空时表示拒绝弹窗打开，元素为待拒申请 id（单条或批量共用） */
  const [rejectModalIds, setRejectModalIds] = useState<string[] | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const pendingItems = items.filter((m) => m.status === "pending");
  const displayItems = listTab === "rejected" ? rejectedItems : pendingItems;
  const pendingCount = pendingItems.length;
  const rejectedCount = rejectedItems.length;
  const allPendingSelected = pendingItems.length > 0 && pendingItems.every((m) => selectedIds.has(m.id));
  const somePendingSelected = listTab === "pending" && pendingItems.some((m) => selectedIds.has(m.id));

  /** 与 AdminDataContext.reload 中的 memberLevelStore 同步；避免首屏仅更新了申请列表而等级尚未写入 store 时这里读到空数组。 */
  const [levels, setLevels] = useState<MemberLevel[]>(() => memberLevelStore.getAll());
  useEffect(() => {
    setLevels(memberLevelStore.getAll());
  }, [storeVersion]);

  useEffect(() => {
    if (!showApprovalPanel && !batchApproving) return;
    const fromStore = memberLevelStore.getAll();
    if (fromStore.length > 0) {
      setLevels(fromStore);
      return;
    }
    let cancelled = false;
    void fetchMemberLevelsForTenant(tenantLegacyId)
      .then((ml) => {
        if (cancelled) return;
        memberLevelStore.replaceFromApi(ml);
        setLevels(memberLevelStore.getAll());
      })
      .catch(() => {
        if (!cancelled) setLevels([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showApprovalPanel, batchApproving, tenantLegacyId]);

  /** 等级列表晚于弹窗出现时补全默认选项（避免 levels 曾为空导致 approvalLevel 一直为空）。 */
  useEffect(() => {
    if (!showApprovalPanel || !detailId || levels.length === 0) return;
    const item = items.find((m) => m.id === detailId);
    const valid = Boolean(approvalLevel && levels.some((l) => l.name === approvalLevel));
    if (valid) return;
    const next =
      item?.level && levels.some((l) => l.name === item.level)
        ? item.level
        : (levels[levels.length - 1]?.name ?? "");
    if (!next) return;
    setApprovalLevel(next);
  }, [showApprovalPanel, detailId, levels, items, approvalLevel]);

  useEffect(() => {
    if (!batchApproving || levels.length === 0) return;
    const valid = Boolean(batchLevel && levels.some((l) => l.name === batchLevel));
    if (valid) return;
    setBatchLevel(levels[levels.length - 1]?.name ?? "");
  }, [batchApproving, levels, batchLevel]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allPendingSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingItems.map((m) => m.id)));
    }
  };

  const openBatchApprove = () => {
    setBatchLevel(levels[levels.length - 1]?.name ?? "");
    setBatchApproving(true);
  };

  const confirmBatchApprove = async () => {
    if (!batchLevel) return;
    const toApprove = items.filter((m) => selectedIds.has(m.id) && m.status === "pending");
    const levelInfo = levels.find((l) => l.name === batchLevel);
    const expireDate = new Date();
    expireDate.setFullYear(expireDate.getFullYear() + 1);
    const fmt = (d: Date) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;

    for (const item of toApprove) {
      const memberLegacyId = deriveMemberLegacyId(item.id);
      try {
        await apiPost(`/api/tenants/${tenantLegacyId}/member-applications/${encodeURIComponent(item.id)}/approve`, {
          memberLegacyId,
          levelName: batchLevel,
          welcomePoints: 50,
        });
        await notificationStore.sendMemberApprovalNotif(
          tenantLegacyId,
          memberLegacyId,
          item.name,
          batchLevel,
          fmt(expireDate),
          levelInfo?.annualFee,
        );
      } catch (e) {
        reportError(e);
      }
    }

    setSelectedIds(new Set());
    setBatchApproving(false);
    await reload();
  };

  const openRejectModal = (ids: string[]) => {
    if (ids.length === 0) return;
    setRejectModalIds(ids);
    setRejectReason("");
  };

  const confirmRejectApplications = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      showUserNotice("请填写拒绝理由，该说明将展示在申请人的小程序「会员服务」申请状态中。");
      return;
    }
    if (!rejectModalIds?.length) return;
    for (const id of rejectModalIds) {
      try {
        await apiPost(`/api/tenants/${tenantLegacyId}/member-applications/${encodeURIComponent(id)}/reject`, { reason });
      } catch (e) {
        reportError(e);
      }
    }
    setRejectModalIds(null);
    setSelectedIds(new Set());
    setDetailId(null);
    setShowApprovalPanel(false);
    await reload();
    void loadRejected();
  };

  /** Approve item directly from detail modal */
  const handleApproveFromDetail = async (id: string) => {
    if (!approvalLevel) {
      showUserNotice("请先在上方选择要通过的会员等级，再点击「确认通过」。");
      return;
    }
    const item = items.find((m) => m.id === id);
    const levelInfo = levels.find((l) => l.name === approvalLevel);
    const expireDate = new Date();
    expireDate.setFullYear(expireDate.getFullYear() + 1);
    const fmt = (d: Date) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
    try {
      await apiPost(`/api/tenants/${tenantLegacyId}/member-applications/${encodeURIComponent(id)}/approve`, {
        memberLegacyId: deriveMemberLegacyId(id),
        levelName: approvalLevel,
        welcomePoints: 50,
      });
    } catch (e) {
      reportError(e);
      return;
    }
    let notifOk = true;
    try {
      await notificationStore.sendMemberApprovalNotif(
        tenantLegacyId,
        deriveMemberLegacyId(id),
        item?.name ?? "会员",
        approvalLevel,
        fmt(expireDate),
        levelInfo?.annualFee,
      );
    } catch (e) {
      notifOk = false;
      reportError(e);
    }
    showUserNotice(
      notifOk
        ? `已通过「${item?.name ?? "申请人"}」的入会申请，会员编号与积分已生成。`
        : `已通过「${item?.name ?? "申请人"}」的入会申请，但欢迎通知发送失败，请稍后手动联系会员或检查通知配置。`,
    );
    setDetailId(null);
    setShowApprovalPanel(false);
    await reload();
  };

  const openDetail = (id: string) => {
    const item = [...items, ...rejectedItems].find((m) => m.id === id);
    setDetailId(id);
    setDetailTab("info");
    setShowApprovalPanel(false);
    // Pre-fill level for approval（会员编号在审核通过时生成或沿用历史预分配）
    const defaultLevel = item?.level ?? levels[levels.length - 1]?.name ?? "";
    setApprovalLevel(defaultLevel);
  };

  const detail = [...items, ...rejectedItems].find((m) => m.id === detailId);
  const detailExtra = detail ? normalizeExtraAnswers(detail.extraAnswers) : {};

  useEffect(() => {
    setAuditSigImgFailed(false);
  }, [detailId, detail?.agreementSignatureDataUrl]);
  const orgLabel = enrollmentAssocLabel(basicInfo);
  const sealSource = (basicInfo.shortName?.trim() || basicInfo.name?.trim() || orgLabel).trim();
  const sealMid = sealSource.length > 8 ? Math.ceil(sealSource.length / 2) : Math.min(6, sealSource.length);
  const sealLine1 = sealSource.slice(0, sealMid) || orgLabel.slice(0, 6);
  const sealLine2 = sealSource.length > sealMid ? sealSource.slice(sealMid) : "协会";

  const auditFeeRows =
    detail != null
      ? buildAuditFeePaymentRows(
          basicInfo,
          orgLabel,
          `¥${(levels.find((l) => l.name === detail.level)?.annualFee ?? 98).toFixed(2)}（${detail.level || "会员"}年费）`,
        )
      : [];
  const collectModeOnline = (basicInfo.enrollmentFeeCollectMode ?? 0) === 1;
  const auditFeeAmountYuan = detail ? levels.find((l) => l.name === detail.level)?.annualFee ?? 0 : 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-50">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-gray-900">会员注册审核</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              待审核 {pendingCount} · 已拒绝 {rejectedCount}
            </p>
          </div>
        </div>
        <div className="flex gap-1 mt-4 -mb-px">
          {(
            [
              { key: "pending" as const, label: "待审核", badge: pendingCount },
              { key: "rejected" as const, label: "已拒绝", badge: rejectedCount },
            ] as const
          ).map((t) => {
            const on = listTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setListTab(t.key);
                  setSelectedIds(new Set());
                }}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  on ? "border-red-500 text-red-600" : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                {t.label}
                {t.badge > 0 ? <span className="ml-1.5 text-[11px] font-medium text-orange-600">{t.badge}</span> : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Batch action bar */}
      {somePendingSelected && (
        <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
              <i className="ri-checkbox-multiple-line text-amber-600 text-sm" />
            </div>
            <span className="text-sm font-semibold text-amber-800">
              已选 <strong className="text-amber-600">{selectedIds.size}</strong> 条待审核申请
            </span>
          </div>
          <button
            onClick={openBatchApprove}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg cursor-pointer hover:bg-green-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-shield-star-line" />批量通过
          </button>
          <button
            onClick={() => {
              const ids = items.filter((m) => selectedIds.has(m.id) && m.status === "pending").map((m) => m.id);
              openRejectModal(ids);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded-lg cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
          >
            <i className="ri-close-circle-line" />批量拒绝
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
          >
            取消选择
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {listTab === "pending" && (
                <th className="px-4 py-3 w-10">
                  <button
                    onClick={toggleSelectAll}
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                      allPendingSelected
                        ? "bg-amber-500 border-amber-500"
                        : somePendingSelected
                        ? "bg-amber-100 border-amber-400"
                        : "border-gray-300 hover:border-amber-400"
                    }`}
                  >
                    {allPendingSelected && <i className="ri-check-line text-white text-[10px]" />}
                    {!allPendingSelected && somePendingSelected && <div className="w-2 h-0.5 bg-amber-500 rounded-full" />}
                  </button>
                </th>
              )}
              {(listTab === "rejected"
                ? ["姓名", "性别/血型", "所在单位", "申请时间", "微信绑定", "拒绝理由", "状态", "操作"]
                : ["姓名", "性别/血型", "所在单位", "申请时间", "微信绑定", "签名", "会员编号", "分配等级", "状态", "操作"]
              ).map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rejectedLoading && listTab === "rejected" ? (
              <tr>
                <td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-400">加载已拒绝申请…</td>
              </tr>
            ) : displayItems.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-400">
                  {listTab === "rejected" ? "暂无已拒绝申请" : "暂无待审核申请"}
                </td>
              </tr>
            ) : displayItems.map((m) => {
              const isPending = m.status === "pending";
              const isSelected = selectedIds.has(m.id);
              return (
                <tr key={m.id} className={`hover:bg-gray-50/50 transition-colors ${isSelected ? "bg-amber-50/50" : ""}`}>
                  {listTab === "pending" && (
                  <td className="px-4 py-4">
                    {isPending ? (
                      <button
                        onClick={() => toggleSelect(m.id)}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                          isSelected ? "bg-amber-500 border-amber-500" : "border-gray-300 hover:border-amber-400"
                        }`}
                      >
                        {isSelected && <i className="ri-check-line text-white text-[10px]" />}
                      </button>
                    ) : (
                      <div className="w-4 h-4" />
                    )}
                  </td>
                  )}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-red-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-900">{m.name}</p>
                          {m.submittedForFriend && (
                            <span className="text-[10px] font-medium text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-md">
                              亲友代办
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">{m.phone}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-gray-600 text-xs">{m.gender} / {m.bloodType || "—"}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-gray-500 text-xs max-w-28 truncate block">{m.organization || "—"}</span>
                  </td>
                  <td className="px-4 py-4 text-gray-500 text-xs">{m.applyDate}</td>
                  <td className="px-4 py-4">
                    {(() => {
                      const b = wxBindBadge(m.wxOpenId);
                      return b.bound ? (
                        <span
                          title={b.title}
                          className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full w-fit max-w-[7rem]"
                        >
                          <i className="ri-wechat-fill" style={{ color: "#07C160" }} />
                          {b.label}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">未绑定</span>
                      );
                    })()}
                  </td>
                  {listTab === "rejected" ? (
                    <td className="px-4 py-4">
                      <span className="text-xs text-red-600/90 max-w-[220px] line-clamp-2" title={m.rejectReason || ""}>
                        {m.rejectReason?.trim() || "—"}
                      </span>
                    </td>
                  ) : (
                    <>
                  <td className="px-4 py-4">
                    {m.hasSigned ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full w-fit">
                        <i className="ri-pen-nib-line" />已签名
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full w-fit">
                        <i className="ri-time-line" />未签名
                      </span>
                    )}
                  </td>
                  {/* Member number column */}
                  <td className="px-4 py-4">
                    {m.status === "approved" ? (
                      <span className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded-lg">
                        {m.assignedMemberNo ?? m.memberNo}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {m.status === "approved" ? (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${memberLevelStore.getClassName(m.assignedLevel ?? m.level)}`}>
                        {m.assignedLevel ?? m.level}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                    </>
                  )}
                  <td className="px-4 py-4">
                    <span className={`flex items-center gap-1.5 text-xs font-medium w-fit px-2.5 py-1 rounded-full ${
                      m.status === "pending" ? "bg-amber-50 text-amber-600" :
                      m.status === "approved" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        m.status === "pending" ? "bg-amber-400" : m.status === "approved" ? "bg-green-400" : "bg-red-400"
                      }`} />
                      {m.status === "pending" ? "待审核" : m.status === "approved" ? "已通过" : "已拒绝"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => openDetail(m.id)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                        m.status === "pending"
                          ? "bg-red-600 text-white hover:bg-red-700"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <i className={m.status === "pending" ? "ri-shield-star-line" : "ri-eye-line"} />
                      {m.status === "pending" ? "审核处理" : "查看详情"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Batch Approve Modal ── */}
      {batchApproving && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-multiple-fill text-green-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">批量通过审核</h3>
                  <p className="text-xs text-gray-400 mt-0.5">为选中的 <strong className="text-amber-600">{selectedIds.size}</strong> 名申请人统一分配等级</p>
                </div>
              </div>
              <button onClick={() => setBatchApproving(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
                  <i className="ri-user-line text-gray-400" />待通过申请人
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {items.filter((m) => selectedIds.has(m.id) && m.status === "pending").map((m) => (
                    <span key={m.id} className="text-xs bg-white border border-gray-200 px-2 py-0.5 rounded-full text-gray-700">{m.name}</span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                  <i className="ri-medal-line text-amber-500" />统一分配等级
                </p>
                {levels.length === 0 ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
                    <p className="font-semibold text-amber-800 mb-1">未获取到会员等级列表</p>
                    <p className="text-amber-800/90 leading-relaxed">
                      请先在「系统设置 → 会员等级」中配置等级；若已配置，请关闭本窗口后刷新页面再试。
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {levels.map((lv) => (
                      <button
                        key={lv.id}
                        onClick={() => setBatchLevel(lv.name)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                          batchLevel === lv.name ? "border-green-500 bg-green-50" : "border-gray-100 hover:border-gray-200 bg-white"
                        }`}
                      >
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${batchLevel === lv.name ? "border-green-500 bg-green-500" : "border-gray-300"}`}
                        >
                          {batchLevel === lv.name && <i className="ri-check-line text-white text-xs" />}
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${lv.className}`}>{lv.name}</span>
                        <span className="text-xs text-gray-400 flex-1 truncate">{lv.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2">
                <i className="ri-coin-line text-amber-500 flex-shrink-0 mt-0.5 text-sm" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  每位通过审核的会员将自动获得 <strong>+50 入会欢迎积分</strong>，并自动生成会员编号，同时收到欢迎通知。
                </p>
              </div>
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button onClick={() => setBatchApproving(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                取消
              </button>
              <button
                onClick={confirmBatchApprove}
                disabled={!batchLevel}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-check-double-line" />
                确认批量通过（{selectedIds.size}人）
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject（单条 / 批量）：须填写拒绝理由 ── */}
      {rejectModalIds && rejectModalIds.length > 0 && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl p-6">
            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-close-circle-line text-red-500 text-3xl" />
              </div>
              <h3 className="font-bold text-gray-900 text-base">
                {rejectModalIds.length > 1 ? "批量拒绝入会申请" : "拒绝入会申请"}
              </h3>
              <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                {rejectModalIds.length > 1 ? (
                  <>
                    将拒绝 <strong className="text-red-600">{rejectModalIds.length}</strong> 名申请人，此操作不可撤销。
                  </>
                ) : (
                  "请说明原因，便于申请人修改后重新提交。"
                )}
              </p>
            </div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">拒绝理由（必填，最多 2000 字）</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="例如：证件照片不清晰；请补充缴费凭证；信息与身份证不一致等。"
              rows={4}
              maxLength={2000}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 resize-y min-h-[100px]"
            />
            <p className="text-[11px] text-gray-400 mt-1">{rejectReason.trim().length}/2000 · 将展示在小程序会员服务页</p>
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={() => setRejectModalIds(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap flex items-center justify-center"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => void confirmRejectApplications()}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-check-line" />
                确认拒绝{rejectModalIds.length > 1 ? `（${rejectModalIds.length}人）` : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Unified Detail + Audit Modal ── */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && setDetailId(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-red-500 text-lg" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{detail.name}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${memberLevelStore.getClassName(detail.assignedLevel ?? detail.level)}`}>
                      {detail.status === "approved" ? (detail.assignedLevel ?? detail.level) : detail.level}
                      {detail.status === "approved" && detail.assignedLevel && (
                        <span className="ml-1 text-green-500 font-semibold">（已分配）</span>
                      )}
                    </span>
                    <span className="text-xs text-gray-400">申请于 {detail.applyDate}</span>
                    {detail.hasSigned && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <i className="ri-pen-nib-line" />已完成签名
                      </span>
                    )}
                    {detail.status === "approved" && (detail.assignedMemberNo ?? detail.memberNo) && (
                      <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded-lg">
                        编号：{detail.assignedMemberNo ?? detail.memberNo}
                      </span>
                    )}
                  </div>
                  {detail.submittedForFriend && (
                    <p className="text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded-lg px-2.5 py-1.5 mt-2 leading-relaxed">
                      <i className="ri-user-heart-line mr-0.5" />
                      本申请为<strong>亲友代办</strong>：表单与证件对应上方姓名所示的实际入会人。
                    </p>
                  )}
                  {detail.status === "rejected" && (
                    <p className="text-[11px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5 mt-2 leading-relaxed">
                      <i className="ri-close-circle-line mr-0.5" />
                      拒绝理由：{detail.rejectReason?.trim() || "未填写"}
                    </p>
                  )}
                </div>
              </div>
              <button onClick={() => setDetailId(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-5 pt-3 flex-shrink-0 flex-wrap">
              {([
                ["info", "ri-file-list-3-line", "基本信息 · 登记表"],
                ["payment", "ri-bank-card-line", "缴费凭证"],
                ["signature", "ri-pen-nib-line", "入会协议"],
              ] as [DetailTab, string, string][]).map(([key, icon, label]) => (
                <button
                  key={key}
                  onClick={() => setDetailTab(key)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                    detailTab === key ? "bg-red-600 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  <i className={icon} />{label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {/* Tab: 基本信息 + 登记表（合并，与原型一致） */}
              {detailTab === "info" && detail && (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: "姓名", value: detail.name },
                        { label: "性别", value: detail.gender },
                        { label: "手机号码", value: detail.phone },
                        { label: "电子邮箱", value: detail.email || "—" },
                        {
                          label: "微信绑定",
                          value: detail.wxOpenId?.trim()
                            ? `已绑定（小程序 OpenID）`
                            : "未绑定",
                          sub: detail.wxOpenId?.trim() || undefined,
                        },
                        { label: "所在地区", value: detail.city },
                        { label: "申请日期", value: detail.applyDate },
                      ].map((row) => (
                        <div key={row.label} className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs text-gray-400 mb-0.5">{row.label}</p>
                          <p className="text-sm font-medium text-gray-900">{row.value}</p>
                          {"sub" in row && row.sub ? (
                            <p className="text-[11px] font-mono text-gray-600 mt-1 break-all leading-snug">{row.sub}</p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                    {detail.reason && (
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 mb-1">加入理由</p>
                        <p className="text-sm text-gray-700 leading-relaxed">{detail.reason}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-500 font-semibold whitespace-nowrap">完整登记表（入库字段）</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>

                  <div className="flex items-center gap-2 bg-red-50 rounded-xl px-4 py-3">
                    <i className="ri-file-list-3-line text-red-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-red-800">结构化申请信息</p>
                      <p className="text-xs text-red-600">与会员提交写入库的字段一致（含扩展报名表）</p>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden text-sm">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <p className="text-xs font-semibold text-gray-600">基本字段</p>
                    </div>
                    {(
                      [
                        ["性别", detail.gender],
                        ["证件类型", detail.idType || "—"],
                        ["证件号码", detail.idNumber || "—"],
                        ["所在城市", detail.city || "—"],
                        ["人事所在单位", detail.organization || "—"],
                        ["会员编号（申请）", detail.status === "pending"
                          ? ((detail.memberNo ?? "").trim() || "审核通过后自动生成")
                          : (detail.memberNo || "—")],
                        ["政治面貌", detail.politicalStatus || "—"],
                        ["籍贯", detail.hometown || "—"],
                        ["衣服尺寸", detail.clothingSize || "—"],
                        ["民族", detail.ethnicity || "—"],
                        ["血型", detail.bloodType || "—"],
                        ["紧急联系人", detail.emergencyContact || "—"],
                        ["紧急联系人电话", detail.emergencyContactPhone || "—"],
                        ["申请等级", detail.level || "—"],
                        ["微信绑定（小程序 OpenID）", detail.wxOpenId?.trim() || "—"],
                        ["加入理由", detail.reason || "—"],
                      ] as const
                    ).map(([label, value]) => (
                      <div key={label} className="flex border-b border-gray-100 last:border-b-0">
                        <div className="w-28 flex-shrink-0 bg-gray-50 px-3 py-2 border-r border-gray-100">
                          <span className="text-xs font-medium text-gray-500">{label}</span>
                        </div>
                        <div className="px-3 py-2 flex-1 min-w-0">
                          <span className="text-xs text-gray-800 break-words">{value}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {detail.resumes && detail.resumes.length > 0 && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-600">工作经历 / 简历</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50 text-gray-500">
                              {["开始", "结束", "单位", "职务", "地区"].map((h) => (
                                <th key={h} className="text-left px-3 py-2 font-semibold">
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {detail.resumes.map((r, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="px-3 py-2">{r.startDate || "—"}</td>
                                <td className="px-3 py-2">{r.endDate || "—"}</td>
                                <td className="px-3 py-2">{r.organization || "—"}</td>
                                <td className="px-3 py-2">{r.position || "—"}</td>
                                <td className="px-3 py-2">{r.area || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {Object.keys(detailExtra).length > 0 && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-gray-600">扩展报名表（自定义字段）</p>
                        <span className="text-[10px] text-gray-400">标签来自系统「报名表字段」配置</span>
                      </div>
                      {Object.entries(detailExtra).map(([fid, val]) => (
                        <div key={fid} className="flex border-b border-gray-100 last:border-b-0">
                          <div className="w-36 flex-shrink-0 bg-gray-50 px-3 py-2 border-r border-gray-100">
                            <span className="text-xs font-medium text-gray-500">{enrollmentLabels[fid] || fid}</span>
                            <span className="block text-[10px] text-gray-400 font-mono">{fid}</span>
                          </div>
                          <div className="px-3 py-2 flex-1 min-w-0">
                            <span className="text-xs text-gray-800 break-words">{val}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: 电子签名 */}
              {detailTab === "signature" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 bg-amber-50 rounded-xl px-4 py-3">
                    <i className="ri-file-text-line text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-amber-800">入会协议签署记录</p>
                      <p className="text-xs text-amber-600">
                        申请人已阅读并签署入会协议
                        {detail.agreementSnapshotVersion?.trim()
                          ? `（${detail.agreementSnapshotVersion.trim()}）`
                          : detail.agreementSnapshotContent?.trim()
                            ? "（正文已存档）"
                            : ""}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {[
                      { label: "签署人", value: detail.name },
                      { label: "签署日期", value: detail.applyDate },
                      {
                        label: "协议版本",
                        value:
                          detail.agreementSnapshotVersion?.trim() ||
                          (detail.agreementSnapshotContent?.trim() ? "申请时存档" : "未存档"),
                      },
                    ].map((row) => (
                      <div key={row.label} className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 mb-0.5">{row.label}</p>
                        <p className="font-medium text-gray-900">{row.value}</p>
                      </div>
                    ))}
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-0.5">签署状态</p>
                      {detail.hasSigned ? (
                        <p className="font-medium text-green-600 flex items-center gap-1 text-sm"><i className="ri-check-circle-line" />已完成签名</p>
                      ) : (
                        <p className="font-medium text-amber-600 flex items-center gap-1 text-sm"><i className="ri-time-line" />未完成签名</p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setShowFullAgreement({
                        name: detail.name,
                        date: detail.applyDate,
                        memberNo: detail.assignedMemberNo ?? detail.memberNo,
                        idNumber: detail.idNumber,
                        phone: detail.phone,
                        signatureUrl: detail.agreementSignatureDataUrl?.trim() || null,
                        agreementSnapshot: detail.agreementSnapshotContent?.trim()
                          ? {
                              title: detail.agreementSnapshotTitle,
                              content: detail.agreementSnapshotContent,
                              version: detail.agreementSnapshotVersion,
                            }
                          : null,
                      })
                    }
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-xl text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-file-text-line" />
                    预览完整协议（协议全文 + 签名区）
                  </button>

                  {detail.agreementSignatureDataUrl?.trim() ? (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                        <i className="ri-pen-nib-line text-red-500" />
                        申请人手写签名
                      </p>
                      {auditSigImgFailed ? (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-center text-xs text-amber-800">
                          签名图加载失败（链接失效或跨域限制）。请直接在对象存储或网络面板核对 URL。
                        </div>
                      ) : (
                        <div className="rounded-xl border border-gray-200 bg-white p-2 flex justify-center">
                          <img
                            key={detail.agreementSignatureDataUrl.trim()}
                            src={detail.agreementSignatureDataUrl.trim()}
                            alt="电子签名"
                            className="max-h-36 max-w-full object-contain"
                            onError={() => setAuditSigImgFailed(true)}
                          />
                        </div>
                      )}
                    </div>
                  ) : detail.hasSigned ? (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                        <i className="ri-pen-nib-line text-red-500" />
                        申请人手写签名（系统未存档签名图，以下为示意）
                      </p>
                      <MockSignature />
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center">
                      <i className="ri-time-line text-amber-500 text-2xl block mb-2" />
                      <p className="text-sm font-semibold text-amber-700">申请人尚未完成签名</p>
                    </div>
                  )}
                  <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                    <p className="text-xs font-semibold text-green-700 flex items-center gap-1.5 mb-1">
                      <i className="ri-shield-check-line text-green-600" />行为准则确认
                    </p>
                    <p className="text-xs text-green-600 leading-relaxed">
                      申请人已阅读并承诺遵守《{orgLabel}行为准则》，理解违反准则可能导致会员资格被取消。
                    </p>
                  </div>
                </div>
              )}

              {/* Tab: 缴费凭证 */}
              {detailTab === "payment" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 bg-amber-50 rounded-xl px-4 py-3">
                    <i className="ri-bank-card-line text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-amber-800">入会缴费凭证</p>
                      <p className="text-xs text-amber-600">
                        {collectModeOnline ? "在线支付模式：显示微信在线支付金额" : "申请人上传的转账截图或银行回单"}
                      </p>
                    </div>
                  </div>
                  {!collectModeOnline ? (
                    <div className="border border-gray-200 rounded-xl overflow-hidden text-sm">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-600">收款账户信息</p>
                      </div>
                      {auditFeeRows.map((row) => (
                        <div key={row.label} className="flex border-b border-gray-100 last:border-b-0">
                          <div className="w-24 flex-shrink-0 bg-gray-50 px-3 py-2.5 border-r border-gray-100">
                            <span className="text-xs font-medium text-gray-500">{row.label}</span>
                          </div>
                          <div className="px-3 py-2.5">
                            <span className="text-xs text-gray-800">{row.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {collectModeOnline ? (
                    <div className="space-y-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4">
                      <p className="text-sm font-bold text-emerald-800">
                        已在线微信支付 ¥{Number(auditFeeAmountYuan || 0).toFixed(2).replace(/\.00$/, "")}
                      </p>
                      {detail.enrollmentFeeOutTradeNo?.trim() ? (
                        <p className="text-xs text-emerald-800">
                          商户订单号：<span className="font-mono">{detail.enrollmentFeeOutTradeNo.trim()}</span>
                        </p>
                      ) : null}
                      {detail.enrollmentFeeWxTransactionId?.trim() ? (
                        <p className="text-xs text-emerald-800">
                          微信订单号：<span className="font-mono">{detail.enrollmentFeeWxTransactionId.trim()}</span>
                        </p>
                      ) : null}
                    </div>
                  ) : detail.paymentProofDataUrl?.trim() ? (
                    <div className="rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 max-h-72">
                      <img
                        referrerPolicy="no-referrer"
                        src={detail.paymentProofDataUrl.trim()}
                        alt="缴费凭证"
                        className="w-full object-contain object-top max-h-72"
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-2xl p-8 text-center border-2 border-dashed border-gray-200">
                      <i className="ri-image-2-line text-3xl text-gray-300 block mb-2"></i>
                      <p className="text-sm text-gray-400 font-medium">申请人未上传缴费凭证</p>
                      <p className="text-xs text-gray-300 mt-1">或通过线下方式缴费，请人工核实</p>
                    </div>
                  )}
                  <div className="bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2">
                    <i className="ri-information-line text-amber-500 flex-shrink-0 mt-0.5"></i>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      请核对转账金额与收款账号，确认缴费后再审核通过。如凭证不清晰，请通过电话联系申请人补充提供。
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer — approval panel for pending items */}
            {detail.status === "pending" && (
              <div className="border-t border-gray-100 flex-shrink-0">
                {!showApprovalPanel ? (
                  <div className="flex gap-3 p-4">
                    <button
                      type="button"
                      onClick={() => openRejectModal([detail.id])}
                      className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:border-red-300 hover:text-red-600 transition-colors whitespace-nowrap flex items-center justify-center"
                    >
                      拒绝申请
                    </button>
                    <button
                      onClick={() => setShowApprovalPanel(true)}
                      className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-green-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-shield-star-line" />通过并分配等级
                    </button>
                  </div>
                ) : (
                  <div className="p-4 space-y-3 bg-green-50/50">
                    <p className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                      <i className="ri-shield-star-line text-green-600" />分配等级（会员编号只读）
                    </p>

                    {/* Level selection */}
                    {levels.length === 0 ? (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
                        <p className="font-semibold text-amber-800 mb-1">未获取到会员等级列表</p>
                        <p className="text-amber-800/90 leading-relaxed">
                          请先在「系统设置 → 会员等级」中配置等级；若已配置，请点击后台顶部刷新或关闭本弹窗后重试。
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {levels.map((lv) => (
                          <button
                            key={lv.id}
                            onClick={() => {
                              setApprovalLevel(lv.name);
                            }}
                            className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl border-2 transition-all cursor-pointer text-left ${
                              approvalLevel === lv.name ? "border-green-500 bg-white" : "border-gray-200 bg-white hover:border-green-300"
                            }`}
                          >
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${approvalLevel === lv.name ? "border-green-500 bg-green-500" : "border-gray-300"}`}
                            >
                              {approvalLevel === lv.name && <i className="ri-check-line text-white text-[8px]" />}
                            </div>
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${lv.className}`}>{lv.name}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Member number（历史申请可能已有预分配编号；新申请审核通过时生成） */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                        <i className="ri-fingerprint-2-line text-red-500" />
                        会员编号
                      </label>
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-mono font-semibold text-gray-900 break-all">
                        {(detail?.memberNo ?? "").trim() || "审核通过后自动生成"}
                      </div>
                      <p className="mt-1.5 text-[11px] text-gray-400 leading-relaxed">
                        新申请不再预分配编号；若上方已有编号则为历史数据，通过时将沿用。留空则由系统在通过时按规则自动生成。
                      </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => setShowApprovalPanel(false)}
                        className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap"
                      >
                        返回
                      </button>
                      <button
                        onClick={() => handleApproveFromDetail(detail.id)}
                        disabled={!approvalLevel}
                        className="flex-[2] py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                      >
                        <i className="ri-check-line" />
                        确认通过 · {approvalLevel || "请选择等级"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full Agreement Preview */}
      {showFullAgreement && (
        <AgreementEnrollmentPreviewModal
          tenantLegacyId={tenantLegacyId}
          memberName={showFullAgreement.name}
          memberNo={showFullAgreement.memberNo}
          idNumber={showFullAgreement.idNumber}
          phone={showFullAgreement.phone}
          signDate={showFullAgreement.date}
          orgLabel={orgLabel}
          sealLine1={sealLine1}
          sealLine2={sealLine2}
          variant="audit"
          signatureImageUrl={showFullAgreement.signatureUrl}
          agreementSnapshot={showFullAgreement.agreementSnapshot ?? null}
          onClose={() => setShowFullAgreement(null)}
        />
      )}
    </div>
  );
}
